import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import HiddenPageMessage from '../components/HiddenPageMessage';
import PreviewBanner from '../components/PreviewBanner';
import { useVisibility } from '../hooks/useVisibility';
import { usePreviewMode } from '../hooks/usePreviewMode';
import { ChevronDown, ChevronUp, User, Search, Star, X, Shield, Award, Building2, Users, GraduationCap } from 'lucide-react';
import { appendUtm } from '../lib/utm';
import { EditableText } from '../components/inline/EditableText';
import { EditableRichText } from '../components/inline/EditableRichText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Premium status badge styling - diplomatic design
const getStatusStyle = (status) => {
  switch (status) {
    case 'Confirmed':
      return {
        bg: 'bg-emerald-900/90',
        text: 'text-emerald-100',
        border: 'border-emerald-700',
        icon: '✓',
        label: 'Confirmed'
      };
    case 'Schedule Permitting':
      return {
        bg: 'bg-amber-900/90',
        text: 'text-amber-100',
        border: 'border-amber-700',
        icon: '◈',
        label: 'Schedule Permitting'
      };
    case 'Invited':
    default:
      return {
        bg: 'bg-slate-800/90',
        text: 'text-slate-200',
        border: 'border-slate-600',
        icon: '○',
        label: 'Invited'
      };
  }
};

// Tier configuration with diplomatic styling
const tierConfig = [
  { 
    tier: 1, 
    label: 'Government Leadership', 
    short: 'Government',
    subtitle: 'Ministers & Secretaries',
    icon: Shield,
    accentColor: 'from-amber-600 to-yellow-500'
  },
  { 
    tier: 2, 
    label: 'Congressional Leadership', 
    short: 'Congress',
    subtitle: 'Senators, Representatives & Governors',
    icon: Building2,
    accentColor: 'from-blue-600 to-indigo-500'
  },
  { 
    tier: 3, 
    label: 'Agency & Regulatory Leadership', 
    short: 'Agency',
    subtitle: 'Chairmen & Commissioners',
    icon: Award,
    accentColor: 'from-emerald-600 to-teal-500'
  },
  { 
    tier: 4, 
    label: 'Industry Leadership', 
    short: 'Industry',
    subtitle: 'CEOs & Presidents',
    icon: Users,
    accentColor: 'from-purple-600 to-violet-500'
  },
  { 
    tier: 5, 
    label: 'Industry & Academic Experts', 
    short: 'Experts',
    subtitle: 'Distinguished Thought Leaders',
    icon: GraduationCap,
    accentColor: 'from-rose-600 to-pink-500'
  }
];

const getTierConfig = (tier) => tierConfig.find(t => t.tier === tier) || tierConfig[0];
const getTierLabel = (tier) => getTierConfig(tier).label;

// Status filter options
const statusOptions = ['All', 'Confirmed', 'Invited', 'Schedule Permitting'];

// Decorative line component
const DecorativeLine = ({ className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
    <div className="w-2 h-2 rotate-45 bg-amber-500/60"></div>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
  </div>
);

// Bio Modal Component - Premium Design
const BioModal = ({ speaker, isOpen, onClose }) => {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    closeButtonRef.current?.focus();
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen || !speaker) return null;

  const statusStyle = getStatusStyle(speaker.status);

  return (
    <div 
      className="fixed inset-0 bg-[#001a2e]/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bio-modal-title"
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-sm shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-modalIn border-t-4 border-amber-500"
      >
        {/* Modal Header */}
        <div className="relative bg-gradient-to-br from-[#001428] via-[#002244] to-[#001a2e] p-8">
          {/* Decorative corner accents */}
          <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-amber-500/30"></div>
          <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-amber-500/30"></div>
          
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-sm transition-all"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-start gap-6">
            {/* Speaker Photo */}
            <div className="w-28 h-28 rounded-sm overflow-hidden border-2 border-amber-500/40 flex-shrink-0 shadow-xl">
              {speaker.image_url && !imageError ? (
                <img
                  src={speaker.image_url}
                  alt={speaker.name}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#002244]">
                  <User className="w-14 h-14 text-amber-500/40" />
                </div>
              )}
            </div>
            
            {/* Speaker Info */}
            <div className="flex-1 min-w-0">
              <h2 id="bio-modal-title" className="text-2xl font-serif font-semibold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {speaker.name}
              </h2>
              <p className="text-amber-400 font-medium text-sm mb-1">
                {speaker.title}
              </p>
              <p className="text-slate-400 text-sm mb-3">
                {speaker.organization}
              </p>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-sm ${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border}`}>
                <span className="text-[10px]">{statusStyle.icon}</span>
                {statusStyle.label}
              </span>
            </div>
          </div>
        </div>
        
        {/* Modal Body */}
        <div className="p-8 overflow-y-auto max-h-[50vh] bg-gradient-to-b from-slate-50 to-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-amber-500"></div>
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-[0.2em]">Biography</h3>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>
          <div className="text-slate-700 leading-relaxed whitespace-pre-line font-light">
            <EditableRichText
              scope={{ type: 'speaker', speakerId: speaker.id, field: 'bio' }}
              value={speaker.bio || "Biography forthcoming."}
              className="min-h-[3em]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Single Featured Speaker - Wide Layout (Premium)
const FeaturedSpeakerWide = ({ speaker, onShowBio }) => {
  const [imageError, setImageError] = useState(false);
  const statusStyle = getStatusStyle(speaker.status);

  return (
    <div className="relative bg-gradient-to-br from-[#001428] via-[#002244] to-[#001a2e] rounded-sm overflow-hidden shadow-2xl border border-amber-500/20">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-amber-500/20"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-amber-500/20"></div>
      <div className="absolute top-6 right-6 opacity-10">
        <Star className="w-24 h-24 text-amber-500" />
      </div>
      
      <div className="flex flex-col lg:flex-row relative z-10">
        {/* Image */}
        <div className="lg:w-2/5 relative">
          <div className="aspect-[4/5] lg:aspect-auto lg:h-full">
            {speaker.image_url && !imageError ? (
              <img
                src={speaker.image_url}
                alt={speaker.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#001a2e] min-h-[400px]">
                <User className="w-32 h-32 text-amber-500/20" />
              </div>
            )}
          </div>
          {/* Featured Badge */}
          <div className="absolute top-6 left-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-sm font-semibold shadow-lg">
              <Star className="w-4 h-4 fill-current" />
              <span className="uppercase tracking-wider text-xs">Featured Speaker</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:w-3/5 p-10 lg:p-14 flex flex-col justify-center">
          {/* Status Badge */}
          <div className="mb-6">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm ${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border}`}>
              <span className="text-[10px]">{statusStyle.icon}</span>
              {statusStyle.label}
            </span>
          </div>
          
          {/* Name - Serif font for authority */}
          <h2 className="text-4xl lg:text-5xl font-serif font-semibold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {speaker.name}
          </h2>
          
          {/* Title with decorative line */}
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-px bg-amber-500"></div>
            <p className="text-xl text-amber-400 font-light tracking-wide">
              {speaker.title}
            </p>
          </div>
          
          <p className="text-lg text-slate-400 mb-8">
            {speaker.organization}
          </p>
          
          {/* Bio Preview */}
          {speaker.bio && speaker.bio !== "Bio coming soon." && (
            <div className="border-l-2 border-amber-500/40 pl-6">
              <p className="text-slate-300 leading-relaxed font-light line-clamp-4 italic">
                "{speaker.bio}"
              </p>
              {speaker.bio.length > 250 && (
                <button
                  onClick={() => onShowBio(speaker)}
                  className="mt-4 text-amber-400 hover:text-amber-300 font-medium text-sm flex items-center gap-2 transition-colors group"
                >
                  <span>Read Full Biography</span>
                  <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Featured Speaker Card (Grid Layout - 2+ featured)
// Standardized card with fixed dimensions and consistent layout
const FeaturedSpeakerCard = ({ speaker, onShowBio }) => {
  const [imageError, setImageError] = useState(false);
  const statusStyle = getStatusStyle(speaker.status);

  return (
    <div 
      className="relative bg-gradient-to-br from-[#001428] via-[#002244] to-[#001a2e] rounded-sm overflow-hidden shadow-xl border border-amber-500/20 group"
      data-testid={`featured-card-${speaker.id}`}
      style={{
        width: '100%',
        maxWidth: '420px',
        height: '200px',
        margin: '0 auto'
      }}
    >
      {/* Decorative corner */}
      <div className="absolute top-0 right-0 w-20 h-20 border-r-2 border-t-2 border-amber-500/20"></div>
      
      {/* Card inner layout: Image Column + Content Column */}
      <div className="flex flex-row h-full">
        {/* Image Column - Fixed Width */}
        <div className="relative flex-shrink-0" style={{ width: '176px', paddingLeft: '12px', paddingTop: '12px' }}>
          {/* Image Container - Fixed size */}
          <div 
            className="relative overflow-hidden"
            style={{
              width: '152px',
              height: '152px',
              borderRadius: '6px'
            }}
          >
            {speaker.image_url && !imageError ? (
              <img
                src={speaker.image_url}
                alt={speaker.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center top'
                }}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#001a2e]">
                <User className="w-16 h-16 text-amber-500/20" />
              </div>
            )}
          </div>
          
          {/* Featured Badge - Bottom-left of image, overlapping edge */}
          <div 
            className="absolute z-10"
            style={{
              bottom: '24px',
              left: '8px'
            }}
          >
            <div 
              className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-xs font-semibold"
              style={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              }}
            >
              <Star className="w-3 h-3 fill-current" />
              <span className="uppercase tracking-wider text-[10px]">Featured</span>
            </div>
          </div>
        </div>

        {/* Content Column - Flex grow */}
        <div 
          className="flex flex-col py-3 pr-4 pl-3"
          style={{ flex: '1 1 0%', minWidth: 0 }}
        >
          {/* Status Tag */}
          <span className={`self-start inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-sm mb-2 ${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border}`}>
            <span className="text-[10px]">{statusStyle.icon}</span>
            {statusStyle.label}
          </span>
          
          {/* Speaker Name */}
          <h3 
            className="text-lg font-serif font-semibold text-white mb-1 leading-tight truncate" 
            style={{ fontFamily: "'Playfair Display', serif" }}
            title={speaker.name}
          >
            {speaker.name}
          </h3>
          
          {/* Title Line */}
          <p className="text-amber-400 font-light text-sm mb-0.5 truncate" title={speaker.title}>
            {speaker.title}
          </p>
          
          {/* Organization/State Line */}
          <p className="text-slate-400 text-sm truncate mb-3" title={speaker.organization}>
            {speaker.organization}
          </p>
          
          {/* CTA Button - After a small margin */}
          <button
            onClick={() => onShowBio(speaker)}
            className="self-start px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/30 text-white text-sm font-medium rounded-sm transition-all flex items-center gap-2 group"
          >
            <span>View Biography</span>
            <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Featured Speakers Section with Carousel
const FeaturedSpeakersSection = ({ featuredSpeakers, onShowBio }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Auto-advance carousel every 3 seconds when more than 3 speakers
  useEffect(() => {
    if (featuredSpeakers.length <= 3) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredSpeakers.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [featuredSpeakers.length]);
  
  if (!featuredSpeakers || featuredSpeakers.length === 0) return null;

  if (featuredSpeakers.length === 1) {
    return (
      <div className="mb-16" data-testid="featured-section-single">
        <FeaturedSpeakerWide speaker={featuredSpeakers[0]} onShowBio={onShowBio} />
      </div>
    );
  }

  // For 2-3 speakers, show all without carousel
  if (featuredSpeakers.length <= 3) {
    return (
      <div className="mb-16" data-testid="featured-section-multiple">
        <div className="mb-8 text-center">
          <p className="text-amber-600 text-sm font-semibold uppercase tracking-[0.3em] mb-2">Distinguished Voices</p>
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#001a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Featured Speakers
          </h2>
          <DecorativeLine className="max-w-xs mx-auto mt-4" />
        </div>
        
        <div 
          className={`grid grid-cols-1 ${featuredSpeakers.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6 items-start`}
          style={{ 
            maxWidth: featuredSpeakers.length === 2 ? '880px' : '1320px',
            margin: '0 auto',
            padding: '0 16px'
          }}
        >
          {featuredSpeakers.map((speaker) => (
            <div 
              key={`featured-${speaker.id}`}
              className="flex justify-center"
              style={{ height: '200px' }}
            >
              <FeaturedSpeakerCard 
                speaker={speaker} 
                onShowBio={onShowBio}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // For 4-6 speakers, show carousel with 3 visible at a time
  const getVisibleSpeakers = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % featuredSpeakers.length;
      visible.push({ speaker: featuredSpeakers[index], position: i });
    }
    return visible;
  };

  const visibleSpeakers = getVisibleSpeakers();

  return (
    <div className="mb-16" data-testid="featured-section-carousel">
      <div className="mb-8 text-center">
        <p className="text-amber-600 text-sm font-semibold uppercase tracking-[0.3em] mb-2">Distinguished Voices</p>
        <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#001a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
          Featured Speakers
        </h2>
        <DecorativeLine className="max-w-xs mx-auto mt-4" />
      </div>
      
      {/* Carousel Container - Fixed height to prevent jumping */}
      <div 
        className="relative overflow-visible"
        style={{ height: '260px', paddingTop: '20px' }} /* Extra height for scaled center card */
      >
        <div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-center"
          style={{ 
            maxWidth: '1320px',
            margin: '0 auto',
            padding: '0 16px'
          }}
        >
          {visibleSpeakers.map(({ speaker, position }) => {
            const isCenter = position === 1; // Middle card (0, 1, 2)
            return (
              <div 
                key={`featured-${speaker.id}-${currentIndex}-${position}`}
                className="flex justify-center transition-all duration-500 ease-out"
                style={{
                  height: '200px',
                  transform: isCenter ? 'scale(1.15)' : 'scale(0.95)',
                  zIndex: isCenter ? 10 : 1,
                  opacity: isCenter ? 1 : 0.85
                }}
              >
                <FeaturedSpeakerCard 
                  speaker={speaker} 
                  onShowBio={onShowBio}
                />
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Carousel Indicators */}
      <div className="flex justify-center gap-2 mt-8">
        {featuredSpeakers.map((_, index) => (
          <button
            key={`indicator-${index}`}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-amber-500 w-6' 
                : 'bg-slate-300 hover:bg-slate-400'
            }`}
            aria-label={`Go to speaker ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Carousel Animation Styles */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0.7;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

// Premium Speaker Card Component
const SpeakerCard = ({ speaker, onShowBio }) => {
  const [imageError, setImageError] = useState(false);
  const statusStyle = getStatusStyle(speaker.status);

  return (
    <div 
      className="group bg-white rounded-sm shadow-md overflow-hidden transition-all duration-500 hover:shadow-xl border border-slate-100 hover:border-amber-200 flex flex-col"
      data-testid={`speaker-card-${speaker.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
        {speaker.image_url && !imageError ? (
          <img
            src={speaker.image_url}
            alt={speaker.name}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#001a2e] to-[#002244]">
            <User className="w-24 h-24 text-amber-500/30" />
          </div>
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#001a2e]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        {/* Status Badge - Top right */}
        <div className="absolute top-3 right-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-sm shadow-lg ${statusStyle.bg} ${statusStyle.text} border ${statusStyle.border}`}>
            <span className="text-[10px]">{statusStyle.icon}</span>
            {statusStyle.label}
          </span>
        </div>

        {/* Featured indicator */}
        {speaker.is_featured && (
          <div className="absolute top-3 left-3">
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-amber-600 to-yellow-500 rounded-sm shadow-lg">
              <Star className="w-4 h-4 text-white fill-current" />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 border-t-2 border-amber-500/20 flex flex-col flex-grow">
        <h3 className="text-lg font-serif font-semibold text-[#001a2e] mb-1.5 leading-tight group-hover:text-amber-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
          {speaker.name}
        </h3>
        <p className="text-sm text-slate-600 font-medium mb-1">
          {speaker.title}
        </p>
        <p className="text-sm text-slate-400 mb-4">
          {speaker.organization}
        </p>

        {/* View Bio Button - Always at bottom */}
        <button
          onClick={() => onShowBio(speaker)}
          className="mt-auto w-full py-2 px-4 bg-slate-50 hover:bg-[#001a2e] border border-slate-200 hover:border-[#001a2e] text-slate-600 hover:text-white text-sm font-medium rounded-sm transition-all duration-300 flex items-center justify-center gap-2"
          data-testid={`view-bio-btn-${speaker.id}`}
        >
          <span>View Bio</span>
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Tier Section Header - Diplomatic Design
const TierSectionHeader = ({ tierNum }) => {
  const config = getTierConfig(tierNum);
  const IconComponent = config.icon;

  return (
    <div className="mb-10">
      <div className="flex items-center gap-4 mb-3">
        <div className={`w-12 h-12 flex items-center justify-center rounded-sm bg-gradient-to-br ${config.accentColor} shadow-lg`}>
          <IconComponent className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#001a2e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {config.label}
          </h2>
          <p className="text-sm text-slate-500 font-light">{config.subtitle}</p>
        </div>
      </div>
      <div className={`h-1 w-32 bg-gradient-to-r ${config.accentColor} rounded-full`}></div>
    </div>
  );
};

// Filter Pill Component - Compact Style
const FilterPill = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 rounded-sm text-xs font-medium transition-all duration-300 whitespace-nowrap border
      ${active 
        ? 'bg-[#001a2e] text-white border-[#001a2e] shadow-sm' 
        : 'bg-white text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-amber-300'
      }`}
    data-testid={`filter-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label}
    {count !== undefined && (
      <span className={`ml-1 ${active ? 'text-white/70' : 'text-slate-400'}`}>
        {count}
      </span>
    )}
  </button>
);

// Main Speakers Page Component
const SpeakersPage = () => {
  const navigate = useNavigate();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bioModalSpeaker, setBioModalSpeaker] = useState(null);
  const [globalContent, setGlobalContent] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTier, setSelectedTier] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  
  // Check page visibility
  const { isPageVisible, getPageHiddenMessage, loading: visibilityLoading } = useVisibility();
  const { preview } = usePreviewMode();
  const [isDraft, setIsDraft] = useState(false);

  // Fetch global content (Register URL editable via /admin/global-content)
  useEffect(() => {
    let ignore = false;
    fetch(`${API_URL}/api/content/global`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!ignore && d) setGlobalContent(d); })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);
  const registrationUrl = globalContent?.registration_url || 'https://utconferences.eventsair.com/cee/registration/Site/Register';

  useEffect(() => {
    let ignore = false;
    window.scrollTo(0, 0);
    (async () => {
      try {
        setLoading(true);
        const url = preview
          ? `${API_URL}/api/speakers?preview=1`
          : `${API_URL}/api/speakers?visible_only=true`;
        const headers = preview
          ? { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
          : {};
        const response = await fetch(url, { headers });
        if (ignore) return;
        if (!response.ok) {
          throw new Error('Failed to fetch speakers');
        }
        const data = await response.json();
        if (ignore) return;
        setSpeakers(data);
        setIsDraft(Array.isArray(data) && data.length > 0 && !!data[0]._preview_isDraft);
        setError(null);
      } catch (err) {
        if (!ignore) {
          console.error('Error fetching speakers:', err);
          setError('Unable to load speakers. Please try again later.');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [preview]);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const url = preview
        ? `${API_URL}/api/speakers?preview=1`
        : `${API_URL}/api/speakers?visible_only=true`;
      const headers = preview
        ? { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
        : {};
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch speakers');
      }
      
      const data = await response.json();
      setSpeakers(data);
      setIsDraft(Array.isArray(data) && data.length > 0 && !!data[0]._preview_isDraft);
      setError(null);
    } catch (err) {
      console.error('Error fetching speakers:', err);
      setError('Unable to load speakers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Seed speakers on first load if none exist
  useEffect(() => {
    const seedSpeakers = async () => {
      if (!loading && speakers.length === 0 && !error) {
        try {
          const response = await fetch(`${API_URL}/api/speakers/seed-initial`, {
            method: 'POST'
          });
          if (response.ok) {
            fetchSpeakers();
          }
        } catch (err) {
          console.error('Error seeding speakers:', err);
        }
      }
    };
    
    seedSpeakers();
  }, [loading, speakers.length, error]);

  const handleShowBio = useCallback((speaker) => {
    setBioModalSpeaker(speaker);
  }, []);

  const handleCloseBioModal = useCallback(() => {
    setBioModalSpeaker(null);
  }, []);

  // Get featured speakers
  const featuredSpeakers = useMemo(() => {
    return speakers
      .filter(s => s.is_featured)
      .sort((a, b) => {
        if (a.order_override !== null && b.order_override !== null) {
          return a.order_override - b.order_override;
        }
        if (a.order_override !== null) return -1;
        if (b.order_override !== null) return 1;
        return a.tier - b.tier;
      });
  }, [speakers]);

  // Filter speakers
  const filteredSpeakers = useMemo(() => {
    return speakers.filter(speaker => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          speaker.name.toLowerCase().includes(query) ||
          speaker.title.toLowerCase().includes(query) ||
          speaker.organization.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      if (selectedTier !== 'All') {
        const tierNum = parseInt(selectedTier);
        if (speaker.tier !== tierNum) return false;
      }
      
      if (selectedStatus !== 'All') {
        if (speaker.status !== selectedStatus) return false;
      }
      
      return true;
    });
  }, [speakers, searchQuery, selectedTier, selectedStatus]);

  // Group filtered speakers by tier
  const groupedSpeakers = useMemo(() => {
    return filteredSpeakers.reduce((acc, speaker) => {
      const tier = speaker.tier;
      if (!acc[tier]) {
        acc[tier] = [];
      }
      acc[tier].push(speaker);
      return acc;
    }, {});
  }, [filteredSpeakers]);

  // Count speakers per tier
  const tierCounts = useMemo(() => {
    const counts = { All: speakers.length };
    speakers.forEach(s => {
      counts[s.tier] = (counts[s.tier] || 0) + 1;
    });
    return counts;
  }, [speakers]);

  // Count speakers per status
  const statusCounts = useMemo(() => {
    const counts = { All: speakers.length };
    speakers.forEach(s => {
      counts[s.status] = (counts[s.status] || 0) + 1;
    });
    return counts;
  }, [speakers]);

  // If page is hidden, show the hidden message (placed after all hooks)
  if (!visibilityLoading && !isPageVisible('speakers')) {
    const message = getPageHiddenMessage('speakers');
    return (
      <HiddenPageMessage
        header={message.header}
        subtitle={message.subtitle}
        body={message.body}
      />
    );
  }

  const hasActiveFilters = searchQuery || selectedTier !== 'All' || selectedStatus !== 'All';

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTier('All');
    setSelectedStatus('All');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <PreviewBanner isDraft={isDraft} draftMeta={isDraft ? { updated_at: new Date().toISOString() } : null} pageLabel="Speakers" />
      <Navigation showBackButton={true} />

      {/* Premium Hero Section */}
      <section className="relative bg-gradient-to-br from-[#001428] via-[#001f3f] to-[#001a2e] text-white py-20 md:py-28 overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-5">
            <div className="absolute top-10 left-10 w-64 h-64 border border-amber-500 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 border border-amber-500 rounded-full"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-amber-500/30 rounded-full"></div>
          </div>
        </div>

        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-40 h-40 border-l-2 border-t-2 border-amber-500/20"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 border-r-2 border-b-2 border-amber-500/20"></div>

        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          {/* Title */}
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-[0.3em] mb-4">World Nuclear Congress</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="speakers-hero-title"
          >
            <EditableText as="span" scope={{ type: 'global', field: 'speakers_hero_title' }} value={globalContent?.speakers_hero_title} multiline={false}>{globalContent?.speakers_hero_title || 'Distinguished Speakers'}</EditableText>
          </h1>
          
          <DecorativeLine className="max-w-md mx-auto mb-6" />
          
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
            Global leaders, policymakers, and visionaries shaping the future of nuclear energy
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 border-4 border-[#001a2e] border-t-amber-500 rounded-full animate-spin mb-6"></div>
              <p className="text-slate-500 font-light">Loading distinguished speakers...</p>
            </div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-red-600 mb-6">{error}</p>
              <button
                onClick={fetchSpeakers}
                className="px-8 py-3 bg-[#001a2e] text-white rounded-sm hover:bg-[#002244] transition-colors font-medium"
              >
                Try Again
              </button>
            </div>
          ) : speakers.length === 0 ? (
            <div className="text-center py-24">
              <User className="w-20 h-20 text-slate-200 mx-auto mb-6" />
              <p className="text-slate-500 text-lg font-light">Speaker announcements coming soon.</p>
            </div>
          ) : (
            <>
              {/* Featured Speakers Section */}
              {featuredSpeakers.length > 0 && !hasActiveFilters && (
                <FeaturedSpeakersSection 
                  featuredSpeakers={featuredSpeakers} 
                  onShowBio={handleShowBio}
                />
              )}

              {/* Filters Section - Below Featured Speakers */}
              <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-12">
                <div className="px-6 py-4">
                  {/* Single line: Count | Category Filters | Status Filters | Search */}
                  <div className="flex items-center gap-4 flex-wrap lg:flex-nowrap">
                    {/* Speaker count */}
                    <p className="text-slate-600 text-sm whitespace-nowrap">
                      <span className="font-semibold text-[#001a2e]">{speakers.length}</span> speakers
                      {hasActiveFilters && (
                        <span className="ml-1 text-slate-400">
                          ({filteredSpeakers.length})
                        </span>
                      )}
                    </p>
                    
                    <div className="hidden lg:block w-px h-6 bg-slate-200"></div>
                    
                    {/* Category Filters */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Category:</span>
                      <FilterPill
                        label="All"
                        active={selectedTier === 'All'}
                        onClick={() => setSelectedTier('All')}
                        count={tierCounts.All}
                      />
                      {tierConfig.map(({ tier, short }) => (
                        tierCounts[tier] > 0 && (
                          <FilterPill
                            key={tier}
                            label={short}
                            active={selectedTier === String(tier)}
                            onClick={() => setSelectedTier(selectedTier === String(tier) ? 'All' : String(tier))}
                            count={tierCounts[tier]}
                          />
                        )
                      ))}
                    </div>

                    <div className="hidden lg:block w-px h-6 bg-slate-200"></div>

                    {/* Status Filters */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Status:</span>
                      {statusOptions.map(status => (
                        statusCounts[status] > 0 && (
                          <FilterPill
                            key={status}
                            label={status}
                            active={selectedStatus === status}
                            onClick={() => setSelectedStatus(selectedStatus === status ? 'All' : status)}
                            count={statusCounts[status]}
                          />
                        )
                      ))}
                    </div>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 whitespace-nowrap"
                        data-testid="clear-filters-btn"
                      >
                        <X className="w-3 h-3" />
                        Clear
                      </button>
                    )}
                    
                    {/* Search Bar - pushed to right */}
                    <div className="relative w-full lg:w-56 lg:ml-auto">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        data-testid="speaker-search-input"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* No results message */}
              {filteredSpeakers.length === 0 && hasActiveFilters && (
                <div className="text-center py-20">
                  <Search className="w-16 h-16 text-slate-200 mx-auto mb-6" />
                  <p className="text-slate-500 text-lg mb-6 font-light">No speakers match your filters</p>
                  <button
                    onClick={clearFilters}
                    className="px-8 py-3 bg-[#001a2e] text-white rounded-sm hover:bg-[#002244] transition-colors font-medium"
                  >
                    Clear Filters
                  </button>
                </div>
              )}

              {/* Render speakers grouped by tier - Congressional Leadership first */}
              {[2, 1, 3, 4, 5].map((tier) => {
                const tierSpeakers = groupedSpeakers[tier];
                if (!tierSpeakers || tierSpeakers.length === 0) return null;
                
                return (
                  <div key={tier} className="mb-20">
                    <TierSectionHeader tierNum={tier} />
                    
                    {/* Speaker Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {tierSpeakers.map((speaker) => (
                        <SpeakerCard 
                          key={speaker.id} 
                          speaker={speaker}
                          onShowBio={handleShowBio}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </section>

      {/* Premium CTA Section */}
      <section className="relative bg-gradient-to-br from-[#001428] via-[#001f3f] to-[#001a2e] py-20 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 border border-amber-500/10 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 border border-amber-500/10 rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-[0.3em] mb-4">Join the Conversation</p>
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Engage with Global Leaders
          </h2>
          
          <DecorativeLine className="max-w-xs mx-auto mb-6" />
          
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto font-light leading-relaxed">
            Register for the World Nuclear Congress and connect with the distinguished speakers, policymakers, and industry leaders shaping the future of nuclear energy.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => window.open(appendUtm(registrationUrl, { utm_medium: 'speakers_cta' }), '_blank')}
              className="px-10 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-[#001a2e] font-semibold rounded-sm hover:from-amber-600 hover:to-yellow-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              data-testid="register-cta-btn"
            >
              Register for WNC
            </button>
            <button
              onClick={() => navigate('/program')}
              className="px-10 py-4 bg-transparent border-2 border-white/30 text-white font-medium rounded-sm hover:bg-white/5 hover:border-amber-500/50 transition-all"
            >
              View Full Program
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Bio Modal */}
      <BioModal 
        speaker={bioModalSpeaker}
        isOpen={!!bioModalSpeaker}
        onClose={handleCloseBioModal}
      />

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modalIn {
          animation: modalIn 0.3s ease-out forwards;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default SpeakersPage;
